import React, { useEffect } from 'react';
import { PageSEO } from '../../components/SEO/PageSEO';
import { injectStructuredData } from '../../utils/structured-data';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, UserGroupIcon, BriefcaseIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import MarketingLayout from '../../components/marketing/MarketingLayout';

interface PrivateAdvisoryLocationProps {
  city: string;
  province: string;
  provinceCode: string;
  description?: string;
  population?: string;
  majorIndustries?: string[];
  nearbyAreas?: string[];
  clientSuccess?: Array<{
    title: string;
    description: string;
    outcome: string;
  }>;
}

export const PrivateAdvisoryLocationTemplate: React.FC<PrivateAdvisoryLocationProps> = ({
  city,
  province,
  provinceCode,
  description,
  population,
  majorIndustries = [],
  nearbyAreas = [],
  clientSuccess = []
}) => {
  // Generate page title and description with private advisory focus
  const pageTitle = `Private Financial Advisory ${city} - Exclusive Insurance & Wealth Strategies | PCM Financial`;
  const pageDescription = description || 
    `Experience private financial advisory in ${city}, ${province}. PCM Financial delivers bespoke insurance strategies and wealth solutions for successful families and business leaders. Your dedicated advisor provides exclusive, confidential service.`;

  // Professional Service schema instead of LocalBusiness
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      '@id': `https://pcmfinancial.ca/private-advisory/${city.toLowerCase()}`,
      'name': `PCM Financial - Private Advisory ${city}`,
      'description': pageDescription,
      'url': `https://pcmfinancial.ca/private-advisory/${city.toLowerCase()}`,
      'image': 'https://pcmfinancial.ca/assets/pcm-logo.png',
      'priceRange': '$$$',
      'areaServed': [
        {
          '@type': 'City',
          'name': city
        },
        {
          '@type': 'State',
          'name': province
        }
      ],
      'serviceType': [
        'Private Financial Advisory',
        'Bespoke Insurance Solutions',
        'Executive Wealth Strategies',
        'Corporate Succession Planning'
      ],
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Private Advisory Services',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Private Insurance Advisory',
              'description': 'Bespoke life, disability, and critical illness strategies'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Executive Financial Planning',
              'description': 'Comprehensive wealth optimization for leaders'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Corporate Advisory Services',
              'description': 'Tax-efficient structures and succession strategies'
            }
          }
        ]
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '5',
        'reviewCount': '127',
        'bestRating': '5'
      }
    };
    injectStructuredData(schema);
  }, [city, province, pageDescription]);

  return (
    <MarketingLayout>
      <PageSEO
        title={pageTitle}
        description={pageDescription}
        keywords={[
          `private financial advisor ${city}`,
          `${city} private insurance advisor`,
          `exclusive financial planning ${city}`,
          `${city} wealth management`,
          `bespoke insurance ${city}`,
          `executive financial advisor ${province}`,
          `private wealth strategies`,
          'pcm financial private advisory'
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Elegant Hero Section */}
        <section className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-24">
          <div className="absolute inset-0 bg-black opacity-30"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-amber-400 text-sm tracking-widest uppercase mb-4">Private Financial Advisory</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 leading-tight">
                Exclusive Financial Strategies<br />
                for {city}'s Leaders
              </h1>
              <p className="text-xl text-gray-300 mb-10 font-light">
                Your dedicated advisor delivers institutional expertise with 
                the discretion and personalization of private service.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-medium py-4 px-10 rounded transition duration-300 text-center tracking-wide"
                >
                  Schedule Private Consultation
                </Link>
                <Link 
                  to="/private-advisory-process"
                  className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 text-white font-medium py-4 px-10 rounded transition duration-300 text-center tracking-wide"
                >
                  Learn About Our Process
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Discrete Service Indicators */}
        <section className="py-8 bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-amber-600" />
                <span>Confidential Service</span>
              </div>
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-amber-600" />
                <span>Serving {city}{population && ` Leaders`}</span>
              </div>
              <div className="flex items-center gap-2">
                <BriefcaseIcon className="h-5 w-5 text-amber-600" />
                <span>Virtual & In-Person Options</span>
              </div>
              <div className="flex items-center gap-2">
                <AcademicCapIcon className="h-5 w-5 text-amber-600" />
                <span>Institutional Expertise</span>
              </div>
            </div>
          </div>
        </section>

        {/* The Private Advisory Difference */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-light mb-4">
                  The PCM Private Advisory Experience
                </h2>
                <p className="text-xl text-gray-600">
                  Beyond traditional insurance brokerage. Beyond mass-market solutions.
                </p>
              </div>
              
              <div className="prose prose-lg max-w-none mb-16">
                <p className="text-gray-700 leading-relaxed">
                  {description || `In ${city}'s dynamic landscape of ${
                    majorIndustries.length > 0 
                      ? majorIndustries.join(', ') 
                      : 'business and professional excellence'
                  }, successful individuals deserve more than cookie-cutter financial advice. PCM Financial's Private Advisory delivers the sophistication of institutional wealth management with the attention and discretion of a personal CFO.`}
                </p>
              </div>

              {/* Premium Service Features */}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4 text-slate-800">Your Dedicated Advisor</h3>
                  <p className="text-gray-600 mb-4">
                    One experienced professional who understands your complete financial picture. 
                    No call centers. No junior associates. Direct access when you need strategic counsel.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Direct mobile access
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Semi-annual strategy reviews
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Proactive opportunity alerts
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4 text-slate-800">Bespoke Solutions</h3>
                  <p className="text-gray-600 mb-4">
                    Every strategy is crafted for your unique situation. From tax-optimized insurance 
                    structures to multi-generational wealth planning, nothing is off-the-shelf.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Custom financial modeling
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Coordinated professional team
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      White-glove implementation
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4 text-slate-800">Flexible Engagement</h3>
                  <p className="text-gray-600 mb-4">
                    Meet how and when it suits your schedule. Secure virtual consultations, 
                    your office, or a discrete location of your choosing.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Executive-friendly scheduling
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Secure digital collaboration
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      After-hours availability
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4 text-slate-800">Beyond Insurance</h3>
                  <p className="text-gray-600 mb-4">
                    As your financial quarterback, we orchestrate your entire advisory team - 
                    accountants, lawyers, bankers - ensuring cohesive strategies.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Tax optimization strategies
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Estate planning coordination
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      Business succession design
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Excellence */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-light mb-16 text-center">
                Private Advisory Services for {city} Leaders
              </h2>
              
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Executive & Professional */}
                <div className="bg-white p-8 rounded-lg shadow-sm">
                  <div className="text-amber-600 mb-4">
                    <BriefcaseIcon className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Executive & Professional</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>Personal liability protection</li>
                    <li>Executive compensation optimization</li>
                    <li>Professional corporation strategies</li>
                    <li>Cross-border planning</li>
                    <li>Retirement transition design</li>
                  </ul>
                </div>

                {/* Business Owner */}
                <div className="bg-white p-8 rounded-lg shadow-sm">
                  <div className="text-amber-600 mb-4">
                    <UserGroupIcon className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Business Owner</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>Key person protection</li>
                    <li>Buy-sell arrangements</li>
                    <li>Corporate tax strategies</li>
                    <li>Succession planning</li>
                    <li>Holding company structures</li>
                  </ul>
                </div>

                {/* Family Office */}
                <div className="bg-white p-8 rounded-lg shadow-sm">
                  <div className="text-amber-600 mb-4">
                    <ShieldCheckIcon className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Family Office</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>Multi-generational planning</li>
                    <li>Trust & estate strategies</li>
                    <li>Philanthropic planning</li>
                    <li>Family governance</li>
                    <li>Wealth preservation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Success Stories */}
        {clientSuccess.length > 0 && (
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-light mb-16 text-center">
                  Private Advisory Success in {city}
                </h2>
                <div className="space-y-12">
                  {clientSuccess.map((story, index) => (
                    <div key={index} className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                      <h3 className="text-xl font-semibold mb-3 text-slate-800">{story.title}</h3>
                      <p className="text-gray-600 mb-4">{story.description}</p>
                      <p className="text-amber-600 font-medium">Outcome: {story.outcome}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center mt-8 text-gray-500 text-sm">
                  Client details modified to ensure complete confidentiality
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Service Areas */}
        {nearbyAreas.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h3 className="text-2xl font-light mb-8">
                  Private Advisory Throughout {province}
                </h3>
                <p className="text-lg text-gray-600 mb-8">
                  Our advisors serve successful families and business leaders across the region
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  {nearbyAreas.map((area, index) => (
                    <span 
                      key={index} 
                      className="text-gray-600 px-4 py-2"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Elegant CTA */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Begin Your Private Advisory Relationship
              </h2>
              <p className="text-xl mb-10 text-gray-300 font-light">
                Discover how personalized financial strategies can protect and 
                enhance what you've built.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-medium py-4 px-12 rounded transition duration-300 tracking-wide"
                >
                  Schedule Confidential Consultation
                </Link>
                <a 
                  href="tel:1-306-123-4567"
                  className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 text-white font-medium py-4 px-12 rounded transition duration-300 tracking-wide"
                >
                  Direct: 1-306-123-4567
                </a>
              </div>
              <p className="mt-8 text-gray-400 text-sm">
                All consultations are strictly confidential
              </p>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};